/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { Auth0Client, User } from "@auth0/auth0-spa-js";
import { Auth0Context } from "./Auth0Context";
import { AppState, Auth0ContextInterface } from "./types";

const DEFAULT_REDIRECT_CALLBACK = (state = {}) =>
  window.history.replaceState(state, window.document.title, window.location.origin);

export const useAuth0 = (): Auth0ContextInterface => useContext(Auth0Context);

type Props = {
  children: ReactNode;
  onRedirectCallback: (state: AppState) => void;
  client: Auth0Client;
};

export function Auth0Provider({ children, onRedirectCallback = DEFAULT_REDIRECT_CALLBACK, client }: Props) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    const isAuthenticatedWithBackend = await client.isAuthenticated();

    setIsAuthenticated(isAuthenticatedWithBackend);

    if (isAuthenticatedWithBackend) {
      const userData = await client.getUser();
      setUser(userData);
    }
  }, [client]);

  useEffect(() => {
    const initAuth0 = async () => {
      if (window.location.search.includes("code=") && window.location.search.includes("state=")) {
        // It is expected, because of lacking typing
        /* eslint-disable @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-argument */
        const { appState } = await client.handleRedirectCallback();
        onRedirectCallback(appState);
        /* eslint-enable @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-argument */
      }

      await client.checkSession();
      await fetchUser();

      setIsLoading(false);
    };
    void initAuth0();
  }, [fetchUser, onRedirectCallback, client]);

  const handleRedirectCallback = useCallback(async () => {
    setIsLoading(true);
    await client.handleRedirectCallback();
    const userData = await client.getUser();
    setIsLoading(false);
    setIsAuthenticated(true);
    setUser(userData);
  }, [client]);

  const value = useMemo(
    () => ({
      isAuthenticated,
      user,
      isLoading,
      handleRedirectCallback,
      checkSession: (...p: Parameters<Auth0Client["checkSession"]>) => client.checkSession(...p),
      getTokenSilently: (...p: Parameters<Auth0Client["getTokenSilently"]>) => client.getTokenSilently(...p),
      getIdTokenClaims: async (...p: Parameters<Auth0Client["getIdTokenClaims"]>) =>
        (await client.getIdTokenClaims(...p)) ?? { __raw: "" },
      loginWithRedirect: (...p: Parameters<Auth0Client["loginWithRedirect"]>) => client.loginWithRedirect(...p),
      logout: (...p: Parameters<Auth0Client["logout"]>) => client.logout(...p),
    }),
    [client, handleRedirectCallback, isAuthenticated, isLoading, user]
  );

  return <Auth0Context.Provider value={value}>{isLoading ? null : children}</Auth0Context.Provider>;
}
